import type { MouseEventHandler, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/cn';

/**
 * Píldora con varias acciones de icono, separadas por un filete.
 *
 * Para la fila que tiene tres o cuatro cosas que hacer y sitio para ninguna
 * etiqueta: la estación del planner, una tarjeta de lista. Cuatro iconos
 * sueltos se leen como adorno; metidos en una sola píldora se leen como la
 * barra de herramientas de esa fila, que es lo que son, y encajan con los
 * chips de al lado.
 *
 * **Lo destructivo se queda fuera.** Borrar no va pegado a «abrir»: se pone
 * como botón aparte, en rojo, a la derecha del grupo.
 *
 * Cada ítem lleva su `Tooltip` (el de `ui/`, no el `title` nativo) y el
 * `aria-label`, así que el icono se explica con ratón, con teclado y en táctil.
 */
export function IconButtonGroup({
  label,
  children,
  className,
}: {
  /** Nombre del grupo para el lector de pantalla: «Station actions». */
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        'inline-flex shrink-0 items-stretch overflow-hidden rounded-full border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

const itemClass =
  'grid h-6 w-[30px] place-items-center border-l border-[var(--color-border-subtle)] first:border-l-0 text-[var(--color-xk-text-muted)] transition-colors hover:bg-[var(--color-xk-accent)]/20 hover:text-[var(--color-accent-ink)] focus-visible:bg-[var(--color-xk-accent)]/20 focus-visible:text-[var(--color-accent-ink)] focus-visible:outline-none disabled:pointer-events-none disabled:opacity-30';

type Common = {
  /** Lo que hace. Es el tooltip y el `aria-label`, así que en imperativo: «Preview the wheel». */
  label: string;
  /** Un icono de lucide a 14 px (`h-3.5 w-3.5`). */
  icon: ReactNode;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
};

type AsLink = Common & { to: string; onClick?: never; disabled?: never };
type AsButton = Common & {
  to?: never;
  onClick: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
};

/** Un ítem del grupo. Con `to` es un enlace; sin él, un botón. */
export function IconButtonGroupItem(props: AsLink | AsButton) {
  const { label, icon, tooltipSide, className } = props;
  const cls = cn(itemClass, className);
  if (props.to !== undefined) {
    return (
      <Tooltip description={label} side={tooltipSide}>
        <Link to={props.to} aria-label={label} className={cls}>
          {icon}
        </Link>
      </Tooltip>
    );
  }
  return (
    <Tooltip description={label} side={tooltipSide}>
      <button
        type="button"
        onClick={props.onClick}
        disabled={props.disabled}
        aria-label={label}
        className={cls}
      >
        {icon}
      </button>
    </Tooltip>
  );
}
